import { Country } from '@axo/config/@types/Country.types';
import { ReactElement, useMemo } from 'react';
import { useCurrentURL } from '../../../hooks/useCurrentURL';
import { TProviders } from '../../../IAuthProviders.types';
import { IAuthProviderUiProps } from '../../../IAuthProviderUiProps.types';
import { useCriiptoProvider } from '../services/useCriiptoProvider';
import { criiptoProvidersViewMap } from './providers';

export type CriiptoLoginProps = IAuthProviderUiProps & {
  country: Extract<Country, 'DK' | 'NO' | 'SE'>;
};

export const CriiptoLogin = ({
  country,
  language,
  action,
  label,
  axoApiUrl,
  onStart,
  onError,
  onSuccess,
  shouldClearHash,
}: CriiptoLoginProps): ReactElement => {
  useCriiptoProvider({ onSuccess, onError, shouldClearHash });

  const UiComponent = criiptoProvidersViewMap[country];

  if (!UiComponent)
    throw new Error(
      `Criipto provider is not mapped to use country: ${country}`
    );

  const currentURL = useCurrentURL();

  const redirectUri = useMemo(() => {
    const url = new URL(currentURL.href);

    if (!url.searchParams.has('provider'))
      url.searchParams.set('provider', 'criipto');

    return url.href;
  }, [currentURL]);

  const authUrl = useMemo(
    () =>
      `${axoApiUrl}/auth/provider/criipto/auth?country=${country}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}`,
    [axoApiUrl, redirectUri, country]
  );

  const handleLoginStart = () => {
    onStart?.(TProviders.Criipto);
  };

  return (
    <UiComponent
      country={country}
      action={action}
      language={language}
      label={label ?? action}
      href={authUrl}
      onClick={handleLoginStart}
    />
  );
};
